/**
 * packages/shared/src/inventory/probe-pins.ts
 *
 * Published identity of the hardware inventory probe (SIL-695) — its versioned
 * filename, CDN path, and the sha256 of its exact bytes.
 *
 * A SIBLING of verification/runner-pins.ts, deliberately not an entry in it.
 * The two are different artifacts on different release cadences serving
 * different audiences: the runner measures and is bumped when measurement
 * changes; this probe reads identity and is bumped when identity collection
 * changes. Folding them into one module would make every runner bump look like
 * a probe bump to anyone reading a diff, and vice versa — and the whole point
 * of splitting the two scripts (SIL-695/696) is that a reviewer can reason
 * about each one alone.
 *
 * SAME IMMUTABILITY RULE as the runner: once published, a version's bytes never
 * change. A change ships as v2 at a new URL with a new digest, because the old
 * digest is pinned inside install commands people have already copied. Git is
 * the permanent record of every version; the CDN is not obliged to serve all of
 * them forever.
 *
 * PUBLISH ORDER MATTERS, and getting it wrong is silent (SIL-688's v8
 * incident): `make deploy-verify-harness` must upload this file to the CDN
 * BEFORE any API deploy from a commit carrying a new pin here, or the API will
 * mint install commands pointing at a URL that 403s.
 */

/**
 * Bumped only when the published bytes change. v1 was the first published probe.
 *
 * v1 -> v2 (SIL-709, SIL-710): states the Linux-only requirement in the script
 * header, where v1 stated it nowhere, and refuses to run under WSL — where v1
 * did not fail but half-succeeded, pairing real GPU serials with the
 * hypervisor's DMI identity and a per-WSL-install machine-id. A comment change
 * alone would still have needed a new version: the bytes changed, so the digest
 * changed, and the digest is pinned inside install commands already copied.
 *
 * v2 -> v3 (SIL-725, SIL-726): two independent fixes, one bump because both
 * touch the same published bytes.
 *   - SIL-725: the lspci fallback matched PCI class 03xx/12xx alone, which
 *     counts the BMC's onboard VGA (ASPEED/Matrox) and a hypervisor's
 *     synthetic display adapter as accelerators alongside any real GPU —
 *     measured on the actual reported case: a GPU-less EC2 instance's own
 *     virtual VGA ([1d0f:1111]). Fixed two ways: a short, sourced denylist of
 *     known non-accelerator [vendor:device] ids subtracted from the class
 *     match in collect.sh (precise), plus a general backstop in assemble.py's
 *     lspci fallback that drops any device whose vendor id isn't a known
 *     accelerator vendor (catches ids nobody has enumerated yet). Neither
 *     narrows the class filter itself (a subclass-only rule risks silently
 *     dropping a REAL accelerator).
 *   - SIL-726: the same WSL reasoning as v1->v2 extended to every other
 *     hypervisor (EC2, VMware, Hyper-V, QEMU/KVM, VirtualBox) — inside any of
 *     them, sysVendor/productName are the hypervisor's identity, not a server
 *     model (measured: EC2 reports sysVendor "Amazon EC2", productName as the
 *     instance type). Detection is `systemd-detect-virt --vm` first
 *     (authoritative — required to correctly pass an EC2 `.metal` instance,
 *     genuine bare metal that still reports sysVendor "Amazon EC2"), DMI as a
 *     fallback only where that binary is absent. Refuses unconditionally,
 *     exit 6, the same as the WSL refusal — no override.
 *
 * v3 -> v4 (SIL-732): two corrections, neither to what is collected.
 *   - The WSL refusal named `inventory-probe.v1.ps1` BY VERSION, and the
 *     Windows probe had since become v2.ps1 (SIL-729) — v1 being the version
 *     that still has the phantom device bug and no virtualization guard. A
 *     refusal whose whole job is to send someone to the right script for their
 *     machine was sending them to the wrong version of it. Nothing 404s,
 *     because published bytes are immutable, so the failure was silent.
 *   - PLATFORM: LINUX ONLY is now ENFORCED, not just documented. Every read
 *     fails open by design, so on macOS v3 and earlier exited 0 with every
 *     identity field null — indistinguishable from a real machine with
 *     nothing to report. It now exits 1 and names the Windows probe, mirroring
 *     the check the `.ps1` has always had.
 *
 * The probes no longer name each other by version AT ALL. Making each point at
 * the other's current version was the obvious fix and it is a trap: two
 * artifacts that name each other by version are coupled, so every bump on one
 * side stales the other's pointer, forever — the exact coupling that keeping
 * them independent (SIL-710) was meant to avoid. Writing that test proved it
 * immediately, by failing on the reciprocal the moment this file went to v4.
 * Both now point at the security page, which always lists the current probe for
 * every platform plus the hash the reader needs anyway. `probe-pins.test.ts`
 * asserts neither probe contains a versioned reference to the other.
 *
 * v4 -> v5 (TrueRead debug telemetry): --post now also sends a `debug.output`
 * field — the run's own "[siliconx-inventory] ..." trace, in plain text,
 * capped at 64KiB, with the bearer token redacted by exact value before it
 * leaves the shell variable it lived in for the whole run. This is TEMPORARY
 * instrumentation so the team can diagnose script failures while TrueRead is
 * validated across real-world customer environments — see
 * db/schema/909_inventory_probe_debug_capture.sql for the table it feeds and
 * its removal note. Collection of hardware identity is unchanged; --print's
 * output is unchanged (the field only ever appears in the POST body).
 */
export const INVENTORY_PROBE_VERSION = 'v5';

export const INVENTORY_PROBE_FILENAME = `inventory-probe.${INVENTORY_PROBE_VERSION}.sh`;

/** Served from the same verify-harness distribution as the runner. */
export const INVENTORY_PROBE_PATH = `/runner/${INVENTORY_PROBE_FILENAME}`;

/**
 * sha256 of the published file's exact bytes.
 *
 * Not computed at build time: this is embedded in the install command a seller
 * runs, and the value they check must come from a file that exists in git, not
 * from whatever a build produced. `probe-pins.test.ts` is the drift guard and
 * hashes the real file on disk.
 */
export const INVENTORY_PROBE_SHA256 =
  '5aa15c439b36833f2d51378644f8c885fe3cdb92351d7d97c84c06a6bf01ca24';

/** Byte length, as a second, cheaper signal that the file on disk is the one pinned. */
export const INVENTORY_PROBE_BYTES = 49748;

/**
 * The WINDOWS probe (SIL-710), published beside the Linux one. Neither is the
 * default; the customer picks the one that matches the machine.
 *
 * A second script rather than one portable script, because the whole argument
 * for this probe is that a security team can read it end to end in a few
 * minutes — a file carrying two incompatible implementations doubles what they
 * read and halves how much of it applies to them.
 *
 * Its own version line: the two are independent artifacts and a change to one
 * must not force a republish of the other. `v1` here is unrelated to the shell
 * probe's `v1`.
 *
 * v1 -> v2 (SIL-729): the two fixes that shipped on Linux as the shell probe's
 * v3 and were never applied here. SIL-725: the Win32_VideoController fallback
 * emitted every display adapter, so a GPU-less Windows host reported one
 * phantom device (measured: the Amazon virtual VGA, PCI\VEN_1D0F&DEV_1111, on
 * a t3.small) and classified downstream as accelerator-bearing; it now reports
 * only devices whose PCI vendor is a known accelerator vendor, the same rule
 * assemble.py applies on the same path. SIL-726: v1 had no virtualization
 * guard at all and accepted "Amazon EC2"/"t3.small" as a server's identity;
 * v2 refuses inside a VM with exit 6 and no override, matching the Linux
 * probe. The Windows signal is NOT a port of the DMI table:
 * `HypervisorPresent` is true on a physical host running Hyper-V or merely
 * with VBS enabled, so it is used as a necessary condition only and a positive
 * guest signal is required on top of it — which also gets the `.metal` case
 * right, since an EC2 metal instance is separated from an EC2 VM by the
 * instance type in productName, not by the vendor string.
 *
 * The two probes now record their results for a shared set of behaviour
 * scenario ids, and probe-parity.test.ts fails if the sets or the results
 * differ. That is the structural half of SIL-729: the bug was possible because
 * nothing anywhere related the two probes' behaviour.
 *
 * v2 -> v3 (SIL-732): the mirror image of the Linux probe's stale pointer. v2
 * named `inventory-probe.v3.sh` by version in its non-Windows refusal, so the
 * Linux probe's own bump to v4 staled it immediately. Both probes now point at
 * the security page instead of at each other by version - two artifacts that
 * name each other by version are coupled, and every bump on one side breaks the
 * other's pointer forever, which is the coupling that keeping them independent
 * was supposed to avoid.
 *
 * Also: every em dash in this file is now a plain dash. The refusal messages go
 * to a Windows console (and, under the EC2 harness, to a COM1 serial port),
 * neither of which is UTF-8 - a customer reading a refusal saw `??"` in the
 * middle of the sentence explaining why their machine was rejected.
 *
 * v3 -> v4 (SIL-748): a comment-only change, and the bytes still change, so the
 * digest changes and the version must too - the same reasoning as v1 -> v2.
 *
 * v4 -> v5 (SIL-1032): parity with the Linux probe's token check. `-Post` now
 * rejects a `-Token`/`SILICONX_INVENTORY_TOKEN` value containing any
 * character outside `[A-Za-z0-9._~+/=-]`, before the request is built - same
 * allowlist, same refusal text, same exit-before-any-network-call behaviour
 * the shell probe has always had. Low risk today (minted tokens are
 * `sxinv_` + base64url and the portal validates the shape before handing out
 * the command), but a hand-edited or pasted token had nowhere to be caught on
 * Windows; .NET's own header validation was the only backstop. sh stays at
 * v4 - its bytes are unchanged, and the two version lines are independent.
 *
 * The header explained how to verify this file's checksum on Windows
 * (`Get-FileHash`, since `sha256sum` is a GNU tool) and said NOTHING about
 * execution policy, which is the thing that actually stops the script running.
 * Windows PowerShell 5.1 - what `#Requires -Version 5.1` targets, and what a
 * customer has by default - ships policy Restricted, which refuses every script
 * whatever its origin or signature. Measured on a real Windows host
 * (2026-08-21): the probe died with "running scripts is disabled on this
 * system" before executing a line.
 *
 * The header now names the three non-fixes people reach for first
 * (`Unblock-File`, which only clears the mark-of-the-web; a code signature,
 * which Windows will not accept from Sigstore and which Restricted blocks
 * anyway; and elevation, which is not what execution policy checks), gives the
 * process-scoped `-ExecutionPolicy Bypass` invocation the portal hands out, says
 * why that is preferable to `Set-ExecutionPolicy` at machine or user scope, and
 * admits the one case no command line can fix - a Group-Policy-set policy
 * outranks the command line, and a vendor claiming otherwise is lying.
 *
 * This belongs in the file rather than only in the portal because the whole
 * argument for this probe is that a security team reads it end to end and finds
 * every answer inside. Execution policy is the first question anyone handed a
 * `.ps1` asks, and the file was answering the adjacent one while ignoring it.
 *
 * v5 -> v6 (TrueRead debug telemetry): -Post now also sends a `debug.output`
 * field - the run's own "[siliconx-inventory] ..." trace, in plain text,
 * capped at 64KiB, with the bearer token redacted by exact value before it
 * leaves the in-memory list it lived in for the whole run. This is TEMPORARY
 * instrumentation so the team can diagnose script failures while TrueRead is
 * validated across real-world customer environments - see
 * db/schema/909_inventory_probe_debug_capture.sql for the table it feeds and
 * its removal note. Collection of hardware identity is unchanged; -Print's
 * output is unchanged (the field only ever appears in the POST body).
 *
 * v6 -> v7 (SIL-1133): AMD (amd-smi.exe/rocm-smi.exe), Intel Gaudi (hl-smi.exe)
 * and Intel XPU (xpu-smi.exe) get their own device-source functions, ported
 * from the Linux probe's Python assembler (amd_devices()/gaudi_devices()/
 * xpu_devices()) the same way Get-NvidiaDevices already read nvidia-smi.exe.
 * Before this, any non-NVIDIA accelerator on Windows fell straight to the
 * Win32_VideoController fallback and got serialSource "unavailable" even when
 * the vendor's own tool was installed and a serial was actually readable -
 * weaker hardware-identity data than the identical hardware got on Linux.
 * NVIDIA and the no-vendor-tool fallback are unchanged; the serialSource
 * values these functions emit (amd_smi, sysfs, hl_smi, xpu_smi) are already
 * part of the schema's vocabulary.
 *
 * SIL-1135 (still within v7, pre-publish): Get-AmdDevices's fallback regex
 * treated '=' as a valid "serial:"/"serial=" delimiter with an unrestricted
 * capture, so the "====...====" banner amd-smi/rocm-smi print immediately
 * around the real "Serial Number:" line was misread as a bogus device whose
 * serial was a run of '=' characters - caught by CI's Windows test suite, not
 * by hand. A `(?!=)` lookahead now rejects a captured value that itself
 * starts with '='. No republish/version bump: v7 was introduced in the same
 * PR that had not yet published it, so this is the pin catching up to bytes
 * that were never live - not a case of editing an already-merged filename.
 * `main` is the immutability boundary for signing even before CDN publish:
 * sign-runner.yml signs on push to main and will not re-sign a filename it
 * has already signed, so a second edit to an already-merged runner/* file
 * needs a version bump, never an in-place fix (SIL-1201 v7 incident,
 * 2026-09-22; see CLAUDE.md).
 *
 * v7 -> v8 (SIL-1900): Windows Server 2012 / 2012 R2. -Post now forces TLS 1.2
 * (OR-ed into SecurityProtocol) because the child powershell on 2012 R2
 * defaults to SSL3/TLS1.0 and the API refuses it; v7 only worked on hosts that
 * already defaulted to TLS 1.2. `#Requires -Version` lowered 5.1 -> 4.0
 * (nothing needed 5.1; 2012 R2 ships 4.0). Supported minimum: Server 2012 R2,
 * PowerShell 4.0, .NET 4.5+. Plain Server 2012 (PowerShell 3.0) is
 * unsupported; the portal command preflights it. Collection is unchanged.
 */
export const INVENTORY_PROBE_PS1_VERSION = 'v8';

export const INVENTORY_PROBE_PS1_FILENAME = `inventory-probe.${INVENTORY_PROBE_PS1_VERSION}.ps1`;

export const INVENTORY_PROBE_PS1_PATH = `/runner/${INVENTORY_PROBE_PS1_FILENAME}`;

export const INVENTORY_PROBE_PS1_SHA256 =
  '027630696cb03db327ea6ce661a8c5516c5b574f88a5ed453b018d7cd27d3ac9';

export const INVENTORY_PROBE_PS1_BYTES = 36058;
